import React, { useState, useEffect, useRef } from 'react';
import { cls, nextTick } from '@arco-design/mobile-utils';
import IconClear from '../icon/IconClear';
import { useSystem } from '../_helpers';
export function useInputLogic(props, inputRef) {
  var value = props.value,
      defaultValue = props.defaultValue,
      validator = props.validator,
      onChange = props.onChange,
      onInput = props.onInput,
      className = props.className,
      style = props.style,
      label = props.label,
      required = props.required,
      prepend = props.prepend,
      append = props.append,
      blurBeforeFocus = props.blurBeforeFocus,
      onKeyDown = props.onKeyDown,
      onPressEnter = props.onPressEnter,
      onFocus = props.onFocus,
      onBlur = props.onBlur,
      onClick = props.onClick,
      disabled = props.disabled,
      _props$border = props.border,
      border = _props$border === void 0 ? 'half' : _props$border,
      prefix = props.prefix,
      suffix = props.suffix,
      clearable = props.clearable,
      _props$clearShowType = props.clearShowType,
      clearShowType = _props$clearShowType === void 0 ? 'focus' : _props$clearShowType,
      _props$clearIcon = props.clearIcon,
      clearIcon = _props$clearIcon === void 0 ? /*#__PURE__*/React.createElement(IconClear, null) : _props$clearIcon,
      onClear = props.onClear,
      autoFocus = props.autoFocus;

  var _useState = useState(value || defaultValue || ''),
      inputValue = _useState[0],
      setInputValue = _useState[1];

  var _useState2 = useState(function () {
    return clearShowType === 'always' || clearShowType === 'value' && Boolean(value || defaultValue);
  }),
      showClear = _useState2[0],
      toggleClear = _useState2[1];

  var focusingRef = useRef(false);
  var actualInputValue = value !== void 0 ? value : inputValue;
  var system = useSystem();
  var wrapRef = useRef(null);
  useEffect(function () {
    if (autoFocus) {
      setTimeout(function () {
        inputRef.current && inputRef.current.focus();
      }, 200);
    }
  }, []);
  useEffect(function () {
    // 处理受控模式下的showClear
    // @en Handling showClear in controlled mode
    if (clearShowType === 'value' && value !== void 0) {
      toggleClear(Boolean(value));
    }
  }, [value]);

  function changeValue(nowValue, callback) {
    if (callback === void 0) {
      callback = function callback() {};
    }

    if (nowValue && validator) {
      if (typeof validator === 'function') {
        if (!validator(nowValue)) {
          return;
        }
      } else if (!validator.test(nowValue)) {
        return;
      }
    } // 处理非受控模式下的showClear
    // @en Handling showClear in uncontrolled mode


    if (clearShowType === 'value' && value === void 0) {
      toggleClear(Boolean(nowValue));
    }

    setInputValue(nowValue);
    callback();
  }

  function handleChange(e) {
    var newValue = e.target.value;
    changeValue(newValue, function () {
      onChange && onChange(e, newValue);
    });
  }

  function handleInput(e) {
    var newValue = e.target.value;
    changeValue(newValue, function () {
      onInput && onInput(e, newValue);
    });
  }

  function handleKeyDown(e) {
    if (e.keyCode === 13) {
      onPressEnter && onPressEnter(e);
    }

    onKeyDown && onKeyDown(e);
  }

  function handleFocus(e) {
    nextTick(function () {
      focusingRef.current = true;
      clearShowType === 'focus' && toggleClear(true);
      onFocus && onFocus(e);
    });
  }

  function handleBlur(e) {
    nextTick(function () {
      focusingRef.current = false;
      clearShowType === 'focus' && toggleClear(false);
      onBlur && onBlur(e);
    });
  }

  function handleClick(e) {
    // 安卓才会有键盘切换不过来的问题，ios不开启此项，因为blur之后不能再自动focus
    // @en Android will have the problem that the keyboard cannot be switched. iOS does not enable this, because it can no longer automatically focus after blur.
    if (blurBeforeFocus && system === 'android' && !focusingRef.current) {
      inputRef.current && inputRef.current.blur();
      nextTick(function () {
        inputRef.current && inputRef.current.focus();
      });
    } else {
      inputRef.current && inputRef.current.focus();
    }

    onClick && onClick(e);
  }

  function handleClear(e) {
    // 不展示清除按钮时不触发事件
    // @en No event fired when clear button is not displayed
    if (!clearable || !showClear) {
      return;
    }

    changeValue('', function () {
      onClear && onClear(e); // 当点击clear前是focus时强制执行focus
      // @en Enforce focus when focus is before clicking clear

      if (focusingRef.current) {
        inputRef.current && inputRef.current.focus();
      }
    });
  }

  function renderWrapper(prefixCls, type, children) {
    return /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-container all-border-box " + (className || ''),
      style: style,
      ref: wrapRef
    }, prepend, /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-wrap", type, "border-" + border, system, {
        disabled: disabled
      }, {
        prefix: prefix
      }, {
        suffix: suffix
      })
    }, label || prefix ? /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-prefix"
    }, label ? /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-label", {
        required: required
      })
    }, label) : prefix) : null, children, clearable && showClear ? /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-clear",
      onClick: handleClear
    }, clearIcon) : null, suffix ? /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-suffix"
    }, suffix) : null), append);
  }

  return {
    inputValue: actualInputValue,
    handleChange: handleChange,
    handleInput: handleInput,
    handleKeyDown: handleKeyDown,
    handleFocus: handleFocus,
    handleBlur: handleBlur,
    handleClick: handleClick,
    renderWrapper: renderWrapper,
    wrapRef: wrapRef
  };
}